import React from "react";
import styled from "styled-components";
import LocationOffIcon from "@mui/icons-material/LocationOff";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useDispatch } from "react-redux";
import { getMarket } from "../config/market";
import { getCurrentLocation } from "../redux/userLocationSlice";

/**
 * CoverageBanner — the ONE way every web checkout surface tells the customer a
 * vendor can't deliver to them, as EARLY as possible (shop entry, cart), instead
 * of at the final Confirm step.
 *
 * Reads a `useCoverage()` result. Renders nothing when the vendor delivers, is
 * still checking, or the verdict is genuinely unknown for a benign reason — it
 * only speaks up when there is something the customer must act on:
 *   • out of range → explain + offer to change location / pick another vendor;
 *   • no location set → prompt to set it (the check can't run without it).
 *
 * Presentation-only and design-token light so it reads consistently on every
 * page (Rams: honest, unobtrusive, only as much UI as the message needs).
 */
const CoverageBanner = ({
  deliverable,
  reason,
  distanceKm,
  radius,
  coverageCountry,
  hasLocation,
  status,
  error,
  onRetry,
  onChangeLocation,
  variant = "inline", // 'inline' | 'block'
}) => {
  const dispatch = useDispatch();
  // Delivers or is still checking → say nothing.
  if (deliverable === true) return null;

  const outOfRange = deliverable === false || reason === "OUT_OF_RANGE";
  const needsLocation = !hasLocation || reason === "NO_DROPOFF";
  const failed = status === "failed";

  if (!outOfRange && !needsLocation && !failed) return null;

  if (failed) {
    return (
      <Banner $tone="warn" $variant={variant} role="alert">
        <ErrorOutlineIcon fontSize="small" />
        <Text>
          We couldn’t confirm delivery coverage.
          {error ? " Check your connection, then retry." : ""} Your order cannot continue until
          the check succeeds.
          {onRetry && (
            <Action type="button" onClick={onRetry}>
              Retry
            </Action>
          )}
        </Text>
      </Banner>
    );
  }

  if (needsLocation && !outOfRange) {
    return (
      <Banner $tone="info" $variant={variant} role="status">
        <LocationOffIcon fontSize="small" />
        <Text>
          Set your delivery area to see whether this vendor delivers to you and
          what delivery costs.
          {/* One tap for the phone's own location (the browser asks for
              permission only now, on the shopper's tap), or pick an area. */}
          <Action type="button" onClick={() => dispatch(getCurrentLocation())}>
            Use my location
          </Action>
          {onChangeLocation && (
            <Action type="button" onClick={onChangeLocation}>
              Choose area
            </Action>
          )}
        </Text>
      </Banner>
    );
  }

  const dist = Number.isFinite(Number(distanceKm)) ? `${Number(distanceKm).toFixed(1)} km` : null;
  const rad = Number.isFinite(Number(radius)) ? `${Number(radius)} km` : null;
  const outsideNationalCoverage = reason === "OUTSIDE_NATIONAL_COVERAGE";
  const coverageCountryName = coverageCountry
    ? getMarket(coverageCountry).name
    : "its home country";

  return (
    <Banner $tone="warn" $variant={variant} role="alert">
      <ErrorOutlineIcon fontSize="small" />
      <Text>
        {outsideNationalCoverage ? (
          <>
            This vendor offers nationwide delivery within {coverageCountryName},
            but your selected address is outside that country. Choose an in-country address.
          </>
        ) : (
          <>
            This vendor doesn’t deliver to your location
            {dist && rad ? ` — you’re ${dist} away and they cover ${rad}.` : "."}{" "}
            Choose a location closer to the vendor, or pick a vendor that delivers to you.
          </>
        )}
        {onChangeLocation && (
          <Action type="button" onClick={onChangeLocation}>
            Change location
          </Action>
        )}
      </Text>
    </Banner>
  );
};

const Banner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: ${(p) => (p.$variant === "block" ? "14px 16px" : "10px 12px")};
  border-radius: 10px;
  margin: ${(p) => (p.$variant === "block" ? "12px 0" : "6px 0")};
  font-size: 14px;
  line-height: 1.4;
  border: 1px solid
    ${(p) => (p.$tone === "warn" ? "#f4c7b8" : "#bcd6e6")};
  background: ${(p) => (p.$tone === "warn" ? "#fdf1ec" : "#eef6fb")};
  color: ${(p) => (p.$tone === "warn" ? "#8a2b06" : "#0b4a63")};

  svg {
    margin-top: 1px;
    flex-shrink: 0;
  }
`;

const Text = styled.span`
  flex: 1;
`;

const Action = styled.button`
  margin-left: 6px;
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  font-weight: 600;
  color: inherit;
  text-decoration: underline;
  cursor: pointer;
`;

export default CoverageBanner;
